import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { EmptyState } from '../components/ui/EmptyState';
import { useI18n } from '../hooks/useI18n';

interface DreamEntry {
  id: string;
  dream_text: string;
  interpretation: string;
  created_at: string;
  mood?: string;
}

interface DreamResult {
  interpretation: string;
  symbols: string[];
  guidance: string;
  readingId: string | null;
  text: string;
}

const MOODS = ['✨ Neutro', '😌 Sereno', '😰 Ansioso', '😱 Assustador', '💫 Lúcido', '❤️ Amoroso'];

const LOADING_PHASES = [
  'Analisando os símbolos do seu sonho...',
  'Consultando os arquétipos oníricos...',
  'Decodificando as mensagens do inconsciente...',
  'Preparando a interpretação do seu sonho...',
];

export function DreamJournalPage() {
  useSystemUsageTracker('diario_sonhos');
  const { profile }    = useAuth();
  const { t }          = useI18n();

  const [dreamText,  setDreamText]  = useState('');
  const [mood,       setMood]       = useState('✨ Neutro');
  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState<DreamResult | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [history,       setHistory]       = useState<DreamEntry[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [expanded,      setExpanded]      = useState<string | null>(null);

  useEffect(() => {
    if (profile?.id) loadHistory();
  }, [profile?.id]);

  async function loadHistory() {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('dream_entries')
      .select('id, dream_text, interpretation, created_at, mood')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(15);
    if (data) setHistory(data);
    setHistoryLoaded(true);
  }

  const MIN_CHARS = 30;

  async function interpret() {
    const trimmed = (dreamText ?? '').trim();
    if (!trimmed) return;
    if (trimmed.length < MIN_CHARS) {
      setError(`O relato deve ter pelo menos ${MIN_CHARS} caracteres (faltam ${MIN_CHARS - trimmed.length}).`);
      return;
    }
    setLoading(true);
    setError(null);

    const { data, fromFallback, error: apiError } = await safeInvoke<DreamResult>('dream-journal', {
      body:     { dreamText: trimmed, mood },
      fallback: { interpretation: '', symbols: [], guidance: '', readingId: null, text: '' },
      timeout:  55_000,
    });

    setLoading(false);
    if (fromFallback || !data.text) {
      setError(apiError ?? t('pages.dreamJournal.error'));
      return;
    }
    setResult(data);
    loadHistory();
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.dreamJournal.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.dreamJournal.subtitle')}
        </p>
      </div>

      {!result && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          {/* Mood selector */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
            {MOODS.map(m => (
              <button key={m} type="button" onClick={() => setMood(m)}
                className={mood === m ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
                {m}
              </button>
            ))}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="dream">{t('pages.dreamJournal.dreamLabel')}</label>
            <textarea id="dream" className="form-textarea" value={dreamText}
              onChange={e => setDreamText(e.target.value)}
              placeholder={t('pages.dreamJournal.dreamPlaceholder')}
              rows={6} maxLength={2000}
              style={{ minHeight: '140px' }} />
            <p className="form-hint" style={{ textAlign: 'right', color: dreamText.length < MIN_CHARS && dreamText.length > 0 ? 'var(--color-warn, #f59e0b)' : undefined }}>
              {dreamText.length}/2000{dreamText.length > 0 && dreamText.length < MIN_CHARS ? `, mínimo ${MIN_CHARS}` : ''}
            </p>
          </div>

          <button className="btn btn-primary btn-block" onClick={interpret}
            disabled={loading || (dreamText ?? '').trim().length < MIN_CHARS}>
            {t('pages.dreamJournal.interpretCta')}
          </button>
        </div>
      )}

      {loading && (
        <div className="card">
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
          <br />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={interpret}>
            Tentar novamente
          </button>
        </div>
      )}

      {result && !loading && (
        <div className="animate-fade-in">
          {/* Symbols */}
          {result.symbols.length > 0 && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                {t('pages.dreamJournal.symbolsTitle')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {result.symbols.map((s, i) => (
                  <span key={i} style={{ padding: '0.25rem 0.75rem', backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="card" style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
              {t('pages.dreamJournal.interpretationTitle')}
            </p>
            <div className="reading-text">
              {result.interpretation.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>

          {result.guidance && (
            <div className="card" style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(31,78,95,0.04)', border: '1px solid var(--border-gold)' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                {t('pages.dreamJournal.guidanceTitle')}
              </p>
              <div className="reading-text">
                {result.guidance.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          )}

          <ReadingActions pageName="Diário dos Sonhos" reading={result.text} signEmoji="💭" />
          <ReadingFeedback feature="dream-journal" readingId={result.readingId ?? undefined} />

          <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={() => { setResult(null); setDreamText(''); }}>
            {t('pages.dreamJournal.newEntry')}
          </button>
        </div>
      )}

      {/* Empty history state */}
      {historyLoaded && history.length === 0 && (
        <EmptyState
          emoji="💭"
          title="Nenhum sonho registrado ainda"
          subtitle="Quando você interpretar um sonho, ele aparecerá aqui no seu diário."
        />
      )}

      {/* History, diary format */}
      {history.length > 0 && (
        <section style={{ marginTop: '2rem' }}>
          <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>
            {t('pages.dreamJournal.historyTitle')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {history.map((item, idx) => {
              const entryDate   = new Date(item.created_at);
              const prevDate    = idx > 0 ? new Date(history[idx - 1].created_at) : null;
              const showDateHdr = !prevDate || entryDate.toDateString() !== prevDate.toDateString();
              return (
                <div key={item.id}>
                  {showDateHdr && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      marginTop: idx === 0 ? 0 : '1rem', marginBottom: '0.5rem',
                    }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--petrol)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {entryDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </span>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-subtle)' }} />
                    </div>
                  )}
                  <div className="card" style={{ padding: '0', marginBottom: '0.5rem' }}>
                    <button
                      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}
                      onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.mood && <span style={{ marginRight: '0.5rem' }}>{item.mood.split(' ')[0]}</span>}
                          {item.dream_text.slice(0, 60)}…
                        </p>
                        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                          {entryDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        </div>
                        {expanded === item.id
                          ? <ChevronUp size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          : <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                      </button>
                      {expanded === item.id && (
                        <div className="animate-fade-in" style={{ padding: '0 1rem 1rem', borderTop: '1px solid var(--border-subtle)' }}>
                          <div className="reading-text" style={{ marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
                            {(item.interpretation || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      )}
    </div>
  );
}
